import * as React from "react";
import {InputGroup, OverlayTrigger, Tooltip} from "react-bootstrap";
import {LcdIcon} from "../icon/LcdIcon";
import {Spinner} from "../spinner/Spinner";
import {defineMessages, InjectedIntlProps, injectIntl} from "react-intl";
import {getLanguageAndLocale} from "../../intl/intl";
import {DateUtil} from "../../util/DateUtil";
import ReactDatePicker from "react-datepicker";

let mouseOutTimer = null;

const MESSAGES = defineMessages({
  NONE: {
    id: "studio.property.edit.none",
    defaultMessage: "None"
  }
});

interface TemporalExtentsEditProps {
  name?: string;
  value?: string;
  defaultValue?: Date;
  type?: string;
  onChange: (newValue: string) => void | Promise<void>;
  onBlur?: React.FocusEventHandler<any>;
  readOnly?: boolean;
}

interface TemporalExtentsEditState {
  isMouseOver: boolean;
  isEditing: boolean;
  editingValue: string;
  submitState: "NOT_STARTED" | "SUBMITTING" | "ERROR"; //no explicit FINISHED state, the edit field just goes back to 'display' mode
  submitError: Error;
  lastMouseOut: number;
  lastMouseOver: number;
}

/**
 * A Temporal Extents edit field that can be edited upon mouseover.
 */
class TemporalExtentsEditComponent extends React.Component<TemporalExtentsEditProps & InjectedIntlProps, TemporalExtentsEditState> {
  constructor(props) {
    super(props);
    this.state = {
      isMouseOver: false,
      isEditing: false,
      editingValue: null,
      submitState: "NOT_STARTED",
      submitError: null,
      lastMouseOut: 0,
      lastMouseOver: 0,
    };
  }

  reset = () => {
    this.setState({
      isEditing: false,
      editingValue: null,
      submitState: "NOT_STARTED",
      submitError: null,
    });
  }

  handleMouseOver = () => {
    this.setState({isMouseOver: true, lastMouseOver: Date.now()});
  }

  mouseOutDone = () => {
    //timeout used to make sure MouseOver&MouseOut is in stable state to allow date entry and prevent flickering
    if (mouseOutTimer) {
      clearTimeout(mouseOutTimer);
    }
    if ((Date.now() - this.state.lastMouseOut < 3000 &&
         Date.now() - this.state.lastMouseOver < 3000) ||
        this.state.lastMouseOut === 0 ||
        this.state.isEditing
    ) {
      mouseOutTimer = setTimeout(this.mouseOutDone, 200);
    } else {
      this.setState({isMouseOver: false, lastMouseOut: Date.now()});
    }
  }

  handleMouseOut = () => {
    this.setState({lastMouseOut: Date.now()});
    this.mouseOutDone();
  }

  handleChangeStart = (newValue: Date) => {
    this.handleChange(newValue, true);
  }

  handleChangeEnd = (newValue: Date) => {
    this.handleChange(newValue, false);
  }

  handleChange = (newValue: Date, start?: boolean) => {
    const currentDates = this.state.editingValue ? this.temporalExtentsStringToStartEndDates(this.state.editingValue)
                                                 : {startDate: newValue, endDate: newValue};
    let editingValue = null;
    if (currentDates.startDate && currentDates.endDate) {
      editingValue = start ? `${newValue.toISOString()}/${currentDates.endDate.toISOString()}`
                           : `${currentDates.startDate.toISOString()}/${newValue.toISOString()}`;
    }
    this.setState(prevState => ({
      editingValue,
      submitState: prevState.submitState === "ERROR" ? "NOT_STARTED" : prevState.submitState
    }));
  }

  handleInputFocus = () => {
    setTimeout(() => {
      this.setState(prevState => ({
        isEditing: true,
        editingValue: prevState.submitState === "ERROR" ? prevState.editingValue : this.props.value  //think problem is here
      }));
    }, 500); //timeout used to make sure input happens after blur when switching between start and end date
  }

  handleInputBlur = (blurEvent) => {
    if (this.state.editingValue !== this.props.value && (this.state.submitState !== "ERROR")) {
      this.valueChanged(this.state.editingValue).then(this.reset).catch(this.reset);
    } else {
      this.reset();
    }
    if (this.props.onBlur) {
      this.props.onBlur(blurEvent);
    }
  }

  valueChanged = (newValue: string): Promise<void> => {
    const onChangeResult = this.props.onChange(newValue);
    if (onChangeResult instanceof Promise) {
      this.setState({submitState: "SUBMITTING"});
      const onChangeResultPromise = onChangeResult as Promise<void>;
      return onChangeResultPromise.catch((error) => {
        this.setState({submitState: "ERROR", submitError: error});
        throw error;
      });
    } else {
      return Promise.resolve();
    }
  }

  shouldShowInput = () => {
    const {isMouseOver, isEditing} = this.state;
    return isMouseOver || isEditing;
  }

  escapeKeyPressedListener = (event: KeyboardEvent) => {
    //cancel on ESC, unfortunately ESC keys are only dispatched on document
    if (event.key === 'Escape') {
      //revert editingValue to this.props.value -> no onChange fired
      this.setState({editingValue: this.props.value});
    }
  }

  temporalExtentsStringToStartEndDates = (inputValue: string) => {
    let startDate: Date = null;
    let endDate: Date = null;

    if (inputValue?.includes("/")) {
      const dates = inputValue.toString().split("/");
      if (dates.length === 2) {
        startDate = dates[0] ? new Date(dates[0]) : null;
        endDate = dates[1] ? new Date(dates[1]) : null;
      }
    }

    return {startDate, endDate};
  }

  renderInput = () => {
    const {intl} = this.props;
    const {locale} = getLanguageAndLocale();
    const inputValue = this.state.isEditing ? this.state.editingValue : this.props.value;
    const startEnd = this.temporalExtentsStringToStartEndDates(inputValue);

    if (this.shouldShowInput() && !this.props.readOnly) {
      document.addEventListener("keyup", this.escapeKeyPressedListener);

      return <div>
        <div className={"TemporalExtentsEditComponent"}>
          <ReactDatePicker
              portalId="batchUpdate-datepicker"
              className={"batchUpdate"}
              onChange={this.handleChangeStart}
              onCalendarClose={() => {
                this.handleInputBlur(null)
              }}
              onFocus={this.handleInputFocus}
              selected={startEnd.startDate}
              startDate={startEnd.startDate}
              endDate={startEnd.endDate}
              showMonthDropdown={true}
              showYearDropdown={true}
              scrollableYearDropdown={true}
              showTimeSelect
              locale={locale}
              dateFormat={`${DateUtil.dateFormat()} ${DateUtil.timeFormat()}`}
              timeFormat={DateUtil.timeFormat()}
              timeIntervals={15}
              selectsStart
          />
        </div>
        {/* tslint:disable-next-line:jsx-use-translation-function */}
        <div className={"TemporalExtentsEditComponent"}>/</div>
        <div className={"TemporalExtentsEditComponent"}>
          <ReactDatePicker
              portalId="batchUpdate-datepicker"
              className={"batchUpdate"}
              onChange={this.handleChangeEnd}
              onCalendarClose={() => {
                this.handleInputBlur(null)
              }}
              onFocus={this.handleInputFocus}
              selected={startEnd.endDate}
              startDate={startEnd.startDate}
              endDate={startEnd.endDate}
              showMonthDropdown={true}
              showYearDropdown={true}
              scrollableYearDropdown={true}
              showTimeSelect
              locale={locale}
              dateFormat={`${DateUtil.dateFormat()} ${DateUtil.timeFormat()}`}
              timeFormat={DateUtil.timeFormat()}
              timeIntervals={15}
              selectsEnd
          />
        </div>
      </div>;
    }
    document.removeEventListener("keyup", this.escapeKeyPressedListener);
    const formattedDate = (startEnd.startDate && startEnd.endDate)
                          ? `${startEnd.startDate.toISOString()}/${startEnd.endDate.toISOString()}` : null;
    return (<div className="TemporalExtentsEditDisplay">
      {this.props.children || formattedDate || (this.props.defaultValue) || intl.formatMessage(MESSAGES.NONE)}
    </div>);
  }

  renderAddon = () => {
    if (this.props.readOnly) {
      return null;
    }
    const {submitState, submitError} = this.state;
    switch (submitState) {
    case "NOT_STARTED":
      return null;
    case "SUBMITTING":
      return <InputGroup.Addon><Spinner/></InputGroup.Addon>;
    case "ERROR": {
      const errorMessage = submitError.message;
      const tooltip = <Tooltip id={(this.props.name || "textedit") + "-error-tooltip"}>{errorMessage}</Tooltip>;
      return (
          <InputGroup.Addon>
            <OverlayTrigger placement="top" overlay={tooltip} defaultOverlayShown={true}>
              <LcdIcon icon="alert"/>
            </OverlayTrigger>
          </InputGroup.Addon>
      );
    }
    }
  }

  render() {
    return (
        <InputGroup className="TemporalExtentsEditContainer">
          <div className="TemporalExtentsEditContainer"
               onMouseOver={this.handleMouseOver}
               onMouseOut={this.handleMouseOut}
          >
            {this.renderInput()}{this.renderAddon()}
          </div>
        </InputGroup>
    );
  }
}

export const TemporalExtentsEdit = injectIntl(TemporalExtentsEditComponent);